// apps/client/src/ui/kit/floating.ts — where controls that float by a 3D piece go on screen (decorate's Turn / Put away, DESIGN.md):
// above it when there is room, else below it, else beside it, else clamped into the safe box. The box is the screen between the top
// HUD row and the tray, so a piece at the back wall never pushes its controls under the HUD or off the top (M1 playtest, shots 68a/b).
// Given rectangles to keep clear, the controls first go beside the floor (`outRight` / `outLeft`) so they never take a tap meant for
// it, and never cover the `hard` ones (the tiles around the piece, where the next tap goes) (M1 playtest, 844 × 390).
export type Pt = { x: number; y: number };
export interface FloatAnchors {
  above: Pt;   // the bottom-centre of the controls when they sit above the piece
  below: Pt;   // the top-centre when below it
  right: Pt;   // the left-middle when to its right
  left: Pt;    // the right-middle when to its left
  outRight?: Pt;   // the left-middle when beside the floor, to its right
  outLeft?: Pt;    // the right-middle when beside the floor, to its left
}
export interface FloatBox { top: number; bottom: number; left: number; right: number }
export type FloatSide = 'above' | 'below' | 'right' | 'left' | 'outRight' | 'outLeft';
/** Screen rectangles to keep clear: `soft` when there is room elsewhere (the floor), `hard` always while anything fits. */
export interface FloatAvoid { soft?: readonly FloatBox[]; hard?: readonly FloatBox[] }
/** Air between the controls and a rectangle they keep clear of. */
const GAP = 4;

export function placeFloating(a: FloatAnchors, size: { w: number; h: number }, box: FloatBox, avoid: FloatAvoid = {}): { left: number; top: number; side: FloatSide } {
  const { w, h } = size;
  const fits = (left: number, top: number) => top >= box.top && top + h <= box.bottom && left >= box.left && left + w <= box.right;
  const clampX = (left: number) => Math.max(box.left, Math.min(left, box.right - w));
  const clampY = (top: number) => Math.max(box.top, Math.min(top, box.bottom - h));
  const hits = (left: number, top: number, rs: readonly FloatBox[] = []) => rs.some((r) => left < r.right && left + w > r.left && top < r.bottom && top + h > r.top);
  const tries: [FloatSide, number, number][] = [
    ...(a.outRight ? [['outRight', a.outRight.x, clampY(a.outRight.y - h / 2)] as [FloatSide, number, number]] : []),
    ...(a.outLeft ? [['outLeft', a.outLeft.x - w, clampY(a.outLeft.y - h / 2)] as [FloatSide, number, number]] : []),
    ['above', clampX(a.above.x - w / 2), a.above.y - h],
    ['below', clampX(a.below.x - w / 2), a.below.y],
    ['right', a.right.x, a.right.y - h / 2],
    ['left', a.left.x - w, a.left.y - h / 2],
  ];
  for (const [side, left, top] of tries) if (fits(left, top) && !hits(left, top, avoid.soft) && !hits(left, top, avoid.hard)) return { left, top, side };
  for (const [side, left, top] of tries) if (fits(left, top) && !hits(left, top, avoid.hard)) return { left, top, side };
  // Nothing by the piece fits: just clear of the tiles around it, above, below, then to either side.
  for (const r of avoid.hard ?? []) {
    const cx = clampX((r.left + r.right) / 2 - w / 2), cy = clampY((r.top + r.bottom) / 2 - h / 2);
    const around: [FloatSide, number, number][] = [['above', cx, r.top - GAP - h], ['below', cx, r.bottom + GAP], ['right', r.right + GAP, cy], ['left', r.left - GAP - w, cy]];
    for (const [side, left, top] of around) if (fits(left, top) && !hits(left, top, avoid.hard)) return { left, top, side };
  }
  return { left: clampX(a.above.x - w / 2), top: clampY(a.above.y - h), side: 'above' };
}
